import { Datum as BarDatum } from "../components/statistics/visx/StackedBars";
import { asNumber } from "../utils/objects";
import { Datum as DonutDatum } from "../components/statistics/visx/Donut";
import { Day, Uid } from "./core";

export interface MemberStat {
    memberUid: Uid;
    totalDist: number;
    totalTracks: number;
    max: number;
    avg: number;
    totalJobs: number;
    ontimeCount: number;
    delaysCount: number;
    successCount: number;
    issueCount: number;
    engagedCount: number;
    passiveCount: number;
    totalJobOnsiteTime: number;
    avgJobOnsiteTime: number;
    totalPlaceOnsiteTime: number;
    avgPlaceOnsiteTime: number;
    totalPauseTime: number;
    avgPauseTime: number;
    avgTravelTime: number;
    totalTravelTime: number;
    totalTimeBetweenJobs: number;
    avgTimeBetweenJobs: number;
    totalStops: number;
    jobStopCount: number;
    placeStopCount: number;
    idlingStopCount: number;
    totalClockedTime: number;
    avgClockedTime: number;
    clockedDayCount: number;
    clockInDayCount: number;
    clockOutDayCount: number;
    avgClockInTime: number;
    avgClockOutTime: number;
    timesheetEntryCount: number;
    missingClockOutCount: number;
    jobCountForCalcTimeBetweenJobs: number;
    jobTypes: IdCount[];
    jobTeams: IdCount[];
    // --- Statistics v2 fields (runtime ≥ 4.1.8.29). normalizeMemberStat() fills defaults for older payloads.
    activeDayCount: number;
    maxClockedDayMs: number;
    implausibleDays: Day[];
    missingClockOutDays: Day[];
    daysJobsNoTracks: number;
    jobsNoTracksDays: Day[];
    trackingGapCount: number;
    trackingGapDays: Day[];
    formCount: number;
    checkinCount: number;
    placeVisitCount: number;
    visitsNoCheckin: number;
    visitsNoCheckinDays: Day[];
    jobsDoneNoForm: number;
    jobsDoneNoFormIds: string[];
    issueJobIds: string[];
    scheduledMs: number;
    scheduledNoClockDays: Day[];
    locationPermission: number;
    deviceIssue?: DeviceIssue;
    teamIds: number[];
    // --- increment 2 (runtime ≥ 4.1.8.30): per-member duration distributions; undefined with older backends.
    dist?: Distributions;
    // --- where the clocked time went, at the requested places (ms). 0 on days stored before the split
    // existed, so those days drop out of the share instead of counting as time away.
    clockedAtPlacesTime: number;
    clockedTravelTime: number;
    clockedElsewhereTime: number;
    clockedNoLocationTime: number;
}

export type DeviceIssue = "iosWhileInUse" | "backgroundLocationOff";

/** Nearest-rank percentiles over one kind of duration record (all values in ms, n = record count). */
export type Dist = { n: number; p25: number; p50: number; p75: number; p90: number; max: number };

export type DistKind = "job" | "place" | "pause" | "travel" | "between" | "clocked";
export const DIST_KINDS: DistKind[] = ["job", "place", "pause", "travel", "between", "clocked"];

/** A kind is absent when it has no records (n == 0). */
export type Distributions = Partial<Record<DistKind, Dist>>;

function normalizeDist(raw: unknown): Dist | undefined {
    if (!raw || typeof raw !== "object") {
        return undefined;
    }
    const d = raw as Partial<Dist>;
    const n = Number(d.n) || 0;
    if (n <= 0) {
        return undefined;
    }
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
    return { n, p25: num(d.p25), p50: num(d.p50), p75: num(d.p75), p90: num(d.p90), max: num(d.max) };
}

/** Drop empty/malformed kinds; returns undefined when nothing usable remains (older backend or no records). */
export function normalizeDistributions(raw: unknown): Distributions | undefined {
    if (!raw || typeof raw !== "object") {
        return undefined;
    }
    const out: Distributions = {};
    let any = false;
    DIST_KINDS.forEach(kind => {
        const dist = normalizeDist((raw as Record<string, unknown>)[kind]);
        if (dist) {
            out[kind] = dist;
            any = true;
        }
    });
    return any ? out : undefined;
}

/** n-weighted median of a list of (value, weight) pairs. */
function weightedMedian(pairs: { value: number; n: number }[]): number {
    const sorted = [...pairs].sort((a, b) => a.value - b.value);
    const total = sorted.reduce((sum, p) => sum + p.n, 0);
    let acc = 0;
    for (const p of sorted) {
        acc += p.n;
        if (acc * 2 >= total) {
            return p.value;
        }
    }
    return sorted.length > 0 ? sorted[sorted.length - 1].value : 0;
}

/**
 * Combine per-member distributions for a client-side subset (team facet, team subtotal rows).
 * Approximation: each percentile is the n-weighted median of the members' own percentiles
 * (the raw durations are not on the wire); `n` is the exact sum, `max` the exact max.
 * Prefer the server's combined `dist` whenever the subset is the whole response.
 */
export function mergeDist(list: (Distributions | undefined)[]): Distributions | undefined {
    const out: Distributions = {};
    let any = false;
    DIST_KINDS.forEach(kind => {
        const dists = list.map(d => d?.[kind]).filter((d): d is Dist => !!d && d.n > 0);
        if (dists.length === 0) {
            return;
        }
        if (dists.length === 1) {
            out[kind] = { ...dists[0] };
            any = true;
            return;
        }
        const pick = (key: keyof Dist) => weightedMedian(dists.map(d => ({ value: d[key], n: d.n })));
        out[kind] = {
            n: dists.reduce((sum, d) => sum + d.n, 0),
            p25: pick("p25"),
            p50: pick("p50"),
            p75: pick("p75"),
            p90: pick("p90"),
            max: Math.max(...dists.map(d => d.max)),
        };
        any = true;
    });
    return any ? out : undefined;
}

/** Company-level "is this feature in use (last 90 days)" flags returned with every stats response. */
export interface UsageSignals {
    timesheet: boolean;
    jobs: boolean;
    tracks: boolean;
    places: boolean;
    checkins: boolean;
    forms: boolean;
    workinghours: boolean;
    teams: boolean;
}

export const EMPTY_USAGE: UsageSignals = {
    timesheet: false,
    jobs: false,
    tracks: false,
    places: false,
    checkins: false,
    forms: false,
    workinghours: false,
    teams: false,
};

/** Derive usage from the data itself — fallback when the backend does not send `usage` yet. */
export function usageFromStats(stats: MemberStat[], teamsConfigured: boolean, placesConfigured: boolean): UsageSignals {
    const any = (f: (s: MemberStat) => boolean) => stats.some(f);
    return {
        timesheet: any(s => s.timesheetEntryCount > 0 || s.clockedDayCount > 0),
        jobs: any(s => s.totalJobs > 0),
        tracks: any(s => s.totalTracks > 0 || s.totalDist > 0),
        places: placesConfigured || any(s => s.placeStopCount > 0),
        checkins: any(s => s.checkinCount > 0),
        forms: any(s => s.formCount > 0),
        workinghours: any(s => s.scheduledMs > 0),
        teams: teamsConfigured,
    };
}

export type AttentionRule =
    | "missingClockOut"
    | "implausibleDay"
    | "jobsNoLocation"
    | "shiftNoClockIn"
    | "jobNoForm"
    | "visitNoCheckin"
    | "trackingGap"
    | "jobIssues"
    | "lateArrivals"
    | "lowSiteTime"
    | "highPause"
    | "highTravel"
    | "highDistance"
    | "lowActivity"
    | "devicePermission"
    | "silentPlace";

export type AttentionSeverity = "critical" | "warning" | "info";

export interface AttentionItem {
    rule: AttentionRule;
    severity: AttentionSeverity;
    uids: Uid[];
    days: Day[];
    count: number;
    value?: number;
    baseline?: number;
    baselineKind?: "own" | "team";
    /** device issues, or "never" for a silentPlace item whose place has no recorded visit */
    cause?: DeviceIssue | "never";
    jobs?: string[];
    /** place-dimension items (silentPlace): uids is empty, the place is here */
    placeUids?: Uid[];
}

/** The subject of an item: the member, or the place for place-dimension rules. */
export function attentionSubject(item: AttentionItem): Uid | undefined {
    return item.uids[0] ?? item.placeUids?.[0];
}

export function isPlaceRule(rule: AttentionRule): boolean {
    return rule === "silentPlace";
}

// ---------------------------------------------------------------- places (POST /stats/places)

export interface PlaceDayVisits {
    day: Day;
    visits: number;
    onsiteMs: number;
}

export interface PlaceMemberVisits {
    uid: Uid;
    visits: number;
    onsiteMs: number;
}

export interface PlaceStatEntry {
    placeUid: Uid;
    visits: number;
    visitors: number;
    visitDays: number;
    totalOnsiteMs: number;
    /** visit durations; absent when the backend sends none */
    dist?: Dist;
    checkins: number;
    /** last visit overall (may be after the window); null = no visit in 90 days */
    lastVisitDay: Day | null;
    lastVisitTs: number | null;
    /** only days with visits, ascending */
    byDay: PlaceDayVisits[];
    /** visits desc, capped */
    byMember: PlaceMemberVisits[];
}

export interface SilentPlace {
    placeUid: Uid;
    lastVisitDay: Day | null;
    daysSince: number | null;
}

export interface PlaceCoverage {
    visited: number;
    active: number;
}

export interface PlaceStatsPrev {
    coverage: PlaceCoverage;
    places: Pick<PlaceStatEntry, "placeUid" | "visits" | "totalOnsiteMs" | "dist">[];
}

export interface PlaceStats {
    silentDays: number;
    coverage: PlaceCoverage;
    /** places with >= 1 visit in the window, visits desc */
    places: PlaceStatEntry[];
    /** active places without a visit for >= silentDays, daysSince desc (nulls first) */
    silent: SilentPlace[];
    prev?: PlaceStatsPrev;
    /** "building" while past days of the window or the last-visit lookback are not rolled up yet */
    baseline: "ready" | "building";
}

export const DEFAULT_SILENT_DAYS = 14;

function normalizePlaceEntry(raw: Partial<PlaceStatEntry> & { placeUid: Uid }): PlaceStatEntry {
    return {
        placeUid: raw.placeUid,
        visits: raw.visits || 0,
        visitors: raw.visitors || 0,
        visitDays: raw.visitDays || 0,
        totalOnsiteMs: raw.totalOnsiteMs || 0,
        dist: normalizeDist(raw.dist),
        checkins: raw.checkins || 0,
        lastVisitDay: raw.lastVisitDay ?? null,
        lastVisitTs: raw.lastVisitTs ?? null,
        byDay: (raw.byDay || []).filter(d => !!d && typeof d.day === "number"),
        byMember: (raw.byMember || []).filter(m => !!m && !!m.uid),
    };
}

/** Fill defaults so the place surfaces can rely on every field; returns undefined for a missing payload. */
export function normalizePlaceStats(raw: any): PlaceStats | undefined {
    if (!raw || typeof raw !== "object") {
        return undefined;
    }
    const coverage = (c: any): PlaceCoverage => ({ visited: Number(c?.visited) || 0, active: Number(c?.active) || 0 });
    const places = ((raw.places || []) as any[]).filter(p => !!p && !!p.placeUid).map(normalizePlaceEntry);
    const silent = ((raw.silent || []) as any[]).filter(p => !!p && !!p.placeUid).map(p => ({
        placeUid: p.placeUid as Uid,
        lastVisitDay: typeof p.lastVisitDay === "number" ? p.lastVisitDay as Day : null,
        daysSince: typeof p.daysSince === "number" ? p.daysSince : null,
    }));
    const prev = raw.prev && typeof raw.prev === "object" ? {
        coverage: coverage(raw.prev.coverage),
        places: ((raw.prev.places || []) as any[]).filter(p => !!p && !!p.placeUid).map(p => ({
            placeUid: p.placeUid as Uid,
            visits: p.visits || 0,
            totalOnsiteMs: p.totalOnsiteMs || 0,
            dist: normalizeDist(p.dist),
        })),
    } : undefined;
    return {
        silentDays: Number(raw.silentDays) || DEFAULT_SILENT_DAYS,
        coverage: coverage(raw.coverage),
        places,
        silent,
        prev,
        baseline: raw.baseline === "building" ? "building" : "ready",
    };
}

// ------------------------------------------------------------------ visits (POST /stats/visits)

export type VisitSplit = "type" | "member" | "team" | "place";

export type VisitPlanMode = "single" | "default" | "mixed" | "none";

export interface VisitGroup {
    /** job type id, member uid, team id or place uid as a string ("0" / "" = none) */
    key: string;
    /** closed visits, edges included */
    n: number;
    /** edges excluded; undefined when only edges are left */
    dist?: Dist;
    under: number;
    over: number;
}

/**
 * Visit length: check-in to check-out per job (tapped checks before GPS). The bars hold every
 * closed visit; `dist` and the groups leave out the edges (under `underMs`, over `overMs`).
 */
export interface VisitStats {
    /** closed visits */
    n: number;
    /** checked in, never checked out */
    open: number;
    source: { tap: number; gps: number };
    /** `width` ms per bar; the last bar holds everything from `(counts.length - 1) * width` on */
    bins: { width: number; counts: number[] };
    dist?: Dist;
    edges: { under: number; over: number; underMs: number; overMs: number };
    plan: { mode: VisitPlanMode; seconds?: number; share: number };
    /** job types with closed visits, before the type filter */
    types: { id: number; n: number }[];
    groups?: VisitGroup[];
    prev?: { n: number; bins: { width: number; counts: number[] }; dist?: Dist };
    /** some past days are still being computed */
    partial: boolean;
}

function normalizeBins(raw: any): { width: number; counts: number[] } {
    const counts = Array.isArray(raw?.counts) ? (raw.counts as unknown[]).map(c => Number(c) || 0) : [];
    return { width: Number(raw?.width) || 0, counts };
}

export function normalizeVisitStats(raw: any): VisitStats | undefined {
    if (!raw || typeof raw !== "object" || !raw.bins) {
        return undefined;
    }
    const edges = raw.edges || {};
    const plan = raw.plan || {};
    const mode: VisitPlanMode = ["single", "default", "mixed"].includes(plan.mode) ? plan.mode : "none";
    return {
        n: Number(raw.n) || 0,
        open: Number(raw.open) || 0,
        source: { tap: Number(raw.source?.tap) || 0, gps: Number(raw.source?.gps) || 0 },
        bins: normalizeBins(raw.bins),
        dist: normalizeDist(raw.dist),
        edges: {
            under: Number(edges.under) || 0,
            over: Number(edges.over) || 0,
            underMs: Number(edges.underMs) || 2 * 60_000,
            overMs: Number(edges.overMs) || 8 * 3_600_000,
        },
        plan: { mode, seconds: typeof plan.seconds === "number" ? plan.seconds : undefined, share: Number(plan.share) || 0 },
        types: ((raw.types || []) as any[]).filter(t => !!t).map(t => ({ id: Number(t.id) || 0, n: Number(t.n) || 0 })),
        groups: Array.isArray(raw.groups) ? (raw.groups as any[]).filter(g => !!g).map(g => ({
            key: String(g.key ?? ""),
            n: Number(g.n) || 0,
            dist: normalizeDist(g.dist),
            under: Number(g.under) || 0,
            over: Number(g.over) || 0,
        })) : undefined,
        prev: raw.prev && typeof raw.prev === "object"
            ? { n: Number(raw.prev.n) || 0, bins: normalizeBins(raw.prev.bins), dist: normalizeDist(raw.prev.dist) }
            : undefined,
        partial: raw.partial === true,
    };
}

// ------------------------------------------------------------------ forms

export interface FormMemberSubmissions {
    uid: Uid;
    submissions: number;
}

export interface FormStatEntry {
    templateId: string;
    submissions: number;
    /** distinct members who submitted */
    submitters: number;
    /** newest submission in the window; 0 = unknown */
    lastTs: number;
    /** submissions desc, capped */
    byMember: FormMemberSubmissions[];
}

export interface FormStats {
    /** templates with >= 1 submission in the window, submissions desc */
    forms: FormStatEntry[];
    prev?: { forms: Pick<FormStatEntry, "templateId" | "submissions">[] };
}

export function normalizeFormStats(raw: any): FormStats | undefined {
    if (!raw || typeof raw !== "object") {
        return undefined;
    }
    const entry = (f: any): FormStatEntry => ({
        templateId: String(f.templateId),
        submissions: Number(f.submissions) || 0,
        submitters: Number(f.submitters) || 0,
        lastTs: Number(f.lastTs) || 0,
        byMember: ((f.byMember || []) as any[]).filter(m => !!m && !!m.uid).map(m => ({ uid: m.uid as Uid, submissions: Number(m.submissions) || 0 })),
    });
    const forms = ((raw.forms || []) as any[]).filter(f => !!f && !!f.templateId).map(entry);
    const prev = raw.prev && typeof raw.prev === "object" ? {
        forms: ((raw.prev.forms || []) as any[]).filter(f => !!f && !!f.templateId).map(f => ({ templateId: String(f.templateId), submissions: Number(f.submissions) || 0 })),
    } : undefined;
    return { forms, prev };
}

export interface AttentionMute {
    rule: AttentionRule;
    uid?: Uid;
}

export const ATTENTION_SEVERITY_ORDER: Record<AttentionSeverity, number> = { critical: 0, warning: 1, info: 2 };

/** Fill defaults for fields older backends do not send, so the registry can rely on every field. */
export function normalizeMemberStat(raw: Partial<MemberStat> & { memberUid: Uid }): MemberStat {
    const base = createEmptyMemberStat(raw.memberUid);
    const stat: MemberStat = { ...base, ...raw };
    stat.implausibleDays = stat.implausibleDays || [];
    stat.missingClockOutDays = stat.missingClockOutDays || [];
    stat.jobsNoTracksDays = stat.jobsNoTracksDays || [];
    stat.trackingGapDays = stat.trackingGapDays || [];
    stat.visitsNoCheckinDays = stat.visitsNoCheckinDays || [];
    stat.jobsDoneNoFormIds = stat.jobsDoneNoFormIds || [];
    stat.issueJobIds = stat.issueJobIds || [];
    stat.scheduledNoClockDays = stat.scheduledNoClockDays || [];
    stat.teamIds = stat.teamIds || [];
    stat.jobTypes = stat.jobTypes || [];
    stat.jobTeams = stat.jobTeams || [];
    if (raw.activeDayCount === undefined) {
        // older backend: a member with any activity counts as one active day per bucket
        stat.activeDayCount = hasMemberActivity(stat) ? 1 : 0;
    }
    if (raw.placeVisitCount === undefined) {
        stat.placeVisitCount = stat.placeStopCount;
    }
    stat.dist = normalizeDistributions(raw.dist);
    return stat;
}

export function normalizeMemberStats(raw: MemberStat[] | undefined): MemberStat[] {
    return (raw || []).map(normalizeMemberStat);
}

export function normalizeMemberStatDays(raw: MemberStatDay[] | undefined): MemberStatDay[] {
    return (raw || []).map(d => ({ day: d.day, data: normalizeMemberStats(d.data), dist: normalizeDistributions(d.dist) }));
}

export interface IdCount {
    id: number;
    count: number;
}

export interface MemberStatDay {
    day: Day;
    data: MemberStat[];
    /** combined over the members of that day (exact, from the backend); undefined with older backends */
    dist?: Distributions;
}

export function createEmptyMemberStat(memberUid: Uid): MemberStat {
    return {
        memberUid,
        totalDist: 0,
        totalTracks: 0,
        max: 0,
        avg: 0,
        totalJobs: 0,
        ontimeCount: 0,
        delaysCount: 0,
        successCount: 0,
        issueCount: 0,
        engagedCount: 0,
        passiveCount: 0,
        totalJobOnsiteTime: 0,
        avgJobOnsiteTime: 0,
        totalPlaceOnsiteTime: 0,
        avgPlaceOnsiteTime: 0,
        totalPauseTime: 0,
        avgPauseTime: 0,
        avgTravelTime: 0,
        totalTravelTime: 0,
        totalTimeBetweenJobs: 0,
        avgTimeBetweenJobs: 0,
        totalStops: 0,
        jobStopCount: 0,
        placeStopCount: 0,
        idlingStopCount: 0,
        totalClockedTime: 0,
        avgClockedTime: 0,
        clockedDayCount: 0,
        clockInDayCount: 0,
        clockOutDayCount: 0,
        avgClockInTime: 0,
        avgClockOutTime: 0,
        timesheetEntryCount: 0,
        missingClockOutCount: 0,
        jobCountForCalcTimeBetweenJobs: 0,
        jobTypes: [],
        jobTeams: [],
        activeDayCount: 0,
        maxClockedDayMs: 0,
        implausibleDays: [],
        missingClockOutDays: [],
        daysJobsNoTracks: 0,
        jobsNoTracksDays: [],
        trackingGapCount: 0,
        trackingGapDays: [],
        formCount: 0,
        checkinCount: 0,
        placeVisitCount: 0,
        visitsNoCheckin: 0,
        visitsNoCheckinDays: [],
        jobsDoneNoForm: 0,
        jobsDoneNoFormIds: [],
        issueJobIds: [],
        scheduledMs: 0,
        scheduledNoClockDays: [],
        locationPermission: 0,
        teamIds: [],
        clockedAtPlacesTime: 0,
        clockedTravelTime: 0,
        clockedElsewhereTime: 0,
        clockedNoLocationTime: 0,
    };
}

/** Clocked time with a known whereabouts split: the denominator of "clocked time at places". */
export function clockedSplitTime(stat: MemberStat): number {
    return stat.clockedAtPlacesTime + stat.clockedTravelTime + stat.clockedElsewhereTime + stat.clockedNoLocationTime;
}

export function retrieveDonutData(field: "jobTypes" | "jobTeams", stats: MemberStatDay[], labelFor: (id: number) => string): DonutDatum[] {
    const flatArray = stats.flatMap(s => s.data.flatMap(d => d[field]));
    const map: Map<number, number> = flatArray.reduce((acc, d) => {
        acc.set(d.id, (acc.get(d.id) || 0) + d.count);
        return acc;
    }, new Map<number, number>());
    const counts: IdCount[] = Array.from(map, ([id, count]) => ({ id, count }));
    const total = flatArray.reduce((sum, cur) => sum + cur.count, 0);
    const data: DonutDatum[] = counts.map(d => ({
        label: labelFor(d.id),
        id: d.id,
        count: d.count,
        percent: total > 0 ? Math.round(d.count * 100 / total) : 0
    } as DonutDatum));
    return data;
}

export function calculatePercent(count: number, total: number): number {
    return total > 0 ? Math.round(count * 100 / total) : 0;
}

export function formatPercentWithCount(count: number, total: number): string {
    return total > 0 ? calculatePercent(count, total) + "% (" + count + ")" : "";
}

export function findMemberStat(stats: MemberStat[], memberUid: Uid): MemberStat | undefined {
    return stats.find(stat => stat.memberUid === memberUid);
}

export function hasMemberActivity(stat?: MemberStat): boolean {
    if (!stat) {
        return false;
    }
    return stat.totalDist > 0
        || stat.totalTracks > 0
        || stat.totalJobs > 0
        || stat.totalJobOnsiteTime > 0
        || stat.totalPlaceOnsiteTime > 0
        || stat.totalPauseTime > 0
        || stat.totalTravelTime > 0
        || (stat.totalStops || 0) > 0
        || (stat.totalClockedTime || 0) > 0
        || (stat.clockedDayCount || 0) > 0
        || (stat.timesheetEntryCount || 0) > 0;
}

export function onTimeDelayDonutData(stat: MemberStat, onTimeLabel: string, delayedLabel: string): DonutDatum[] {
    const total = stat.ontimeCount + stat.delaysCount;
    return [
        { id: 1, label: onTimeLabel, count: stat.ontimeCount, percent: calculatePercent(stat.ontimeCount, total) },
        { id: 2, label: delayedLabel, count: stat.delaysCount, percent: calculatePercent(stat.delaysCount, total) },
    ].filter(d => d.count > 0);
}

export function jobOutcomeDataByDay(stats: MemberStatDay[], formatDay: (day: Day) => string): BarDatum[] {
    return stats.map(statDay => {
        const successCount = statDay.data.reduce((sum, stat) => sum + stat.successCount, 0);
        const issueCount = statDay.data.reduce((sum, stat) => sum + stat.issueCount, 0);
        const totalJobs = statDay.data.reduce((sum, stat) => sum + stat.totalJobs, 0);
        return {
            bucketField: formatDay(statDay.day),
            successCount,
            issueCount,
            otherCount: Math.max(totalJobs - successCount - issueCount, 0),
        };
    }).filter(d => asNumber(d, "successCount") + asNumber(d, "issueCount") + asNumber(d, "otherCount") > 0);
}

export function timeTrendDataByDay(stats: MemberStatDay[], formatDay: (day: Day) => string): BarDatum[] {
    return stats.map(statDay => ({
        bucketField: formatDay(statDay.day),
        totalJobOnsiteTime: statDay.data.reduce((sum, stat) => sum + stat.totalJobOnsiteTime, 0),
        totalPlaceOnsiteTime: statDay.data.reduce((sum, stat) => sum + stat.totalPlaceOnsiteTime, 0),
        totalTravelTime: statDay.data.reduce((sum, stat) => sum + stat.totalTravelTime, 0),
    })).filter(d => asNumber(d, "totalJobOnsiteTime") + asNumber(d, "totalPlaceOnsiteTime") + asNumber(d, "totalTravelTime") > 0);
}

export function timeAllocationData(stat: MemberStat): BarDatum[] {
    const data = {
        bucketField: "Time",
        totalJobOnsiteTime: stat.totalJobOnsiteTime,
        totalPlaceOnsiteTime: stat.totalPlaceOnsiteTime,
        totalTravelTime: stat.totalTravelTime,
        totalPauseTime: stat.totalPauseTime,
    };
    return asNumber(data, "totalJobOnsiteTime")
        + asNumber(data, "totalPlaceOnsiteTime")
        + asNumber(data, "totalTravelTime")
        + asNumber(data, "totalPauseTime") > 0 ? [data] : [];
}
